import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface Complaint {
  complaint_id: number;
  customer_contact: string;
  complaint_date: string;
  defect_description: string;
  severity: string;
  status: string;
  cost_impact_estimate: number;
  customer: { name?: string };
  product: { name?: string };
  batch: { batch_number?: string };
}

interface ComplaintEditModalProps {
  complaint: Complaint;
  open: boolean;
  onClose: () => void;
  onStatusUpdate: (id: number, status: string) => void;
}

export const ComplaintEditModal: React.FC<ComplaintEditModalProps> = ({
  complaint,
  open,
  onClose,
  onStatusUpdate,
}) => {
  const [status, setStatus] = useState(complaint.status);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (status === complaint.status) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      await onStatusUpdate(complaint.complaint_id, status);
      onClose();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Register': return 'text-blue-600';
      case 'In Progress': return 'text-yellow-600';
      case 'Resolved': return 'text-green-600';
      case 'Closed': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">
            Edit Complaint #{complaint.complaint_id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Complaint Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Complaint Summary</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-600">Customer:</span>
                <span className="ml-2 text-gray-900">{complaint.customer?.name || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Product:</span>
                <span className="ml-2 text-gray-900">{complaint.product?.name || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Current Status:</span>
                <span className={`ml-2 font-medium ${getStatusColor(complaint.status)}`}>
                  {complaint.status}
                </span>
              </div>
            </div>
          </div>

          {/* Status Update */}
          <div className="space-y-3">
            <Label htmlFor="status" className="text-sm font-semibold text-gray-800">
              Update Status
            </Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Register">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-3"></div>
                    <div>
                      <div className="font-medium">Register</div>
                      <div className="text-xs text-gray-500">Initial registration</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="In Progress">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-3"></div>
                    <div>
                      <div className="font-medium">In Progress</div>
                      <div className="text-xs text-gray-500">Being investigated</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="Resolved">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-3"></div>
                    <div>
                      <div className="font-medium">Resolved</div>
                      <div className="text-xs text-gray-500">Issue resolved</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="Closed">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-gray-500 mr-3"></div>
                    <div>
                      <div className="font-medium">Closed</div>
                      <div className="text-xs text-gray-500">Case closed</div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || status === complaint.status}
            className="rounded-xl bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Updating...
              </div>
            ) : (
              'Update Status'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};